import { useCallback } from 'react'

import {
  checkCustomerEmailApiV1CustomersEmailCheckGet,
  useCreateCustomerApiV1CustomersPost,
  useUpdateCustomerApiV1CustomersCustomerIdPatch,
} from '#/shared/api/generated/shipment/theCargoShipment'

import {
  buildCustomerPayload,
  normalizeEmail,
} from '../lib/to-customer-payload'
import type { CustomerFormData } from './customer-schema'

interface ResolveCustomerArgs {
  form: CustomerFormData
  customerId: string | undefined
  originalEmail: string | undefined
}

/**
 * Persists customer form data and returns the customer id the shipment
 * should reference. Email unchanged → PATCH the current customer; email
 * changed → update the customer the new email resolves to, or create one.
 */
export function useResolveCustomer() {
  const updateCustomer = useUpdateCustomerApiV1CustomersCustomerIdPatch()
  const createCustomer = useCreateCustomerApiV1CustomersPost()

  const resolve = useCallback(
    async ({
      form,
      customerId,
      originalEmail,
    }: ResolveCustomerArgs): Promise<string> => {
      const payload = buildCustomerPayload(form)
      const emailUnchanged =
        normalizeEmail(form.email) === normalizeEmail(originalEmail)
      if (emailUnchanged && customerId) {
        await updateCustomer.mutateAsync({ customerId, data: payload })
        return customerId
      }
      const check = await checkCustomerEmailApiV1CustomersEmailCheckGet({
        email: form.email,
      })
      const matchedId = check.data.exists
        ? (check.data.customer_id ?? null)
        : null
      if (matchedId) {
        await updateCustomer.mutateAsync({
          customerId: matchedId,
          data: payload,
        })
        return matchedId
      }
      const created = await createCustomer.mutateAsync({ data: payload })
      return created.data.id
    },
    [updateCustomer, createCustomer],
  )

  return {
    resolve,
    isResolving: updateCustomer.isPending || createCustomer.isPending,
  }
}
